import React from 'react';
import CompanyHotel from '@app/model/company_hotel/CompanyHotel';
import PricingRoom from '@app/model/pricing-automation/PricingRoom';
import PricingArchitecture from '@app/model/pricing-automation/PricingArchitecture';
import PricingSubmissionItem from '@app/model/pricing-automation/PricingSubmissionItem';
import Utilities from '@util/Utilities';
import PricingArchitectureDerivativeRuleType from '@app/model/pricing-automation/PricingArchitectureDerivativeRuleType';
import { PriceRecommendationState } from '@app/model/market_state/PriceRecommendationState';
import PriceRecommendationRoomState from '@app/model/market_state/PriceRecommendationRoomState';
import { ArrowDownIcon, ArrowUpIcon } from '@heroicons/react/20/solid';
import PricingRoomName from '@app/site/secured/rateshopper/pricing-automation/architecture/component/PricingRoomName';

const PriceSubmissionLogTableFragment: React.FunctionComponent<{
	companyHotel: CompanyHotel;
	pricingRooms: PricingRoom[];
	architectures: PricingArchitecture[];
	statDate: string;
	items: PricingSubmissionItem[];
	recommendationStates: PriceRecommendationState[];
}> = ({ companyHotel, pricingRooms, architectures, statDate, items, recommendationStates }) => {
	const findArchitectureByRoomId = (roomId: number) =>
		architectures.find(architecture => architecture.findRoomById(roomId) !== undefined);
	const findRoomById = (roomId: number) => pricingRooms.find(room => room.id === roomId);
	const architectureGroups = architectures
		.map(architecture => {
			const architectureItems = items.filter(
				item => architecture.findRoomById(item.pricingArchitectureRoomId) !== undefined
			);
			const sortedItems = architectureItems.sort((a, b) => {
				if (a.pricingArchitectureRoomId === architecture.anchor.id) return -1;
				if (b.pricingArchitectureRoomId === architecture.anchor.id) return 1;
				return (architecture.findRoomById(a.pricingArchitectureRoomId)?.roomName ?? '').localeCompare(
					architecture.findRoomById(b.pricingArchitectureRoomId)?.roomName ?? ''
				);
			});
			return {
				architecture,
				items: sortedItems,
			};
		})
		.filter(group => group.items.length > 0);
	const unmappedItems = items
		.filter(item => findArchitectureByRoomId(item.pricingArchitectureRoomId) === undefined)
		.sort((a, b) =>
			(findRoomById(a.pricingArchitectureRoomId)?.roomName ?? '').localeCompare(findRoomById(b.pricingArchitectureRoomId)?.roomName ?? '')
		);
	const findRecommendationStateById = (recommendationStateId: number) =>
		recommendationStates.find(state => state.id === recommendationStateId);

	return (
		<div className="divide-y divide-gray-200 overflow-hidden rounded-md bg-gray-50 ring-1 ring-black ring-opacity-10">
			<div className="px-3 py-3">
				<h6 className="text-sm font-semibold text-gray-900">
					Submitted Rates for {Utilities.formatDateString(statDate, 'DD/MM/yyyy', false)}
				</h6>
			</div>
			<div className="space-y-2 divide-y divide-gray-100 text-sm">
				<div className="overflow-x-auto">
					<div className="inline-block min-w-full align-middle">
						<table className="min-w-full divide-y divide-gray-200 text-xs">
							<thead>
								<tr>
									<th scope="col" className="py-2 px-3 text-left">
										Rooms
									</th>
									<th scope="col" className="py-2 px-3 text-left whitespace-nowrap">
										Current
									</th>
									<th scope="col" className="py-2 px-3 text-left">
										Recomm.
									</th>
									<th scope="col" className="py-2 px-3 text-left">
										Submitted
									</th>
									<th scope="col" className="py-2 px-3 text-left">
										Diff.
									</th>
								</tr>
							</thead>
							{architectureGroups.map(group => (
								<RenderArchitectureSubmissionRows
									key={group.architecture.id}
									companyHotel={companyHotel}
									architecture={group.architecture}
									items={group.items}
									findRecommendationStateById={findRecommendationStateById}
								/>
							))}
							{unmappedItems.length > 0 && (
								<tbody className="divide-y divide-gray-200 bg-white">
									{unmappedItems.map(item => (
										<RenderSubmissionItemRow
											key={item.id}
											companyHotel={companyHotel}
											room={findRoomById(item.pricingArchitectureRoomId)}
											item={item}
											recommendationState={findRecommendationStateById(item.recommendationStateId)}
										/>
									))}
								</tbody>
							)}
						</table>
					</div>
				</div>
			</div>
		</div>
	);
};

const RenderArchitectureSubmissionRows: React.FunctionComponent<{
	companyHotel: CompanyHotel;
	architecture: PricingArchitecture;
	items: PricingSubmissionItem[];
	findRecommendationStateById: (
		recommendationStateId: number
	) => PriceRecommendationState | undefined;
}> = ({ companyHotel, architecture, items, findRecommendationStateById }) => {
	return (
		<tbody className="divide-y divide-gray-200 bg-white">
			{items.map(item => {
				const room = architecture.findRoomById(item.pricingArchitectureRoomId);
				const derivative = architecture.derivatives.find(d => d.pricingRoom.id === item.pricingArchitectureRoomId);
				const recommendationState = findRecommendationStateById(item.recommendationStateId);
				const roomType = Utilities.snakeCaseToWords(
					recommendationState?.roomMappingType ?? architecture.competitorsMappingType
				);
				let derivativeDescription: React.ReactNode | undefined = undefined;
				if (derivative !== undefined) {
					derivativeDescription = renderDerivativeDescription(
						companyHotel,
						derivative.rule.type,
						derivative.rule.value,
						roomType
					);
				}

				return (
					<RenderSubmissionItemRow
						key={item.id}
						companyHotel={companyHotel}
						room={room}
						item={item}
						isAnchor={item.pricingArchitectureRoomId === architecture.anchor.id}
						derivativeDescription={derivativeDescription}
						recommendationState={recommendationState}
						roomType={roomType}
					/>
				);
			})}
		</tbody>
	);
};

const RenderSubmissionItemRow: React.FunctionComponent<{
	companyHotel: CompanyHotel;
	room?: PricingRoom;
	item: PricingSubmissionItem;
	isAnchor?: boolean;
	derivativeDescription?: React.ReactNode;
	recommendationState?: PriceRecommendationState;
	roomType?: string;
}> = ({
	companyHotel,
	room,
	item,
	isAnchor,
	derivativeDescription,
	recommendationState,
	roomType,
}) => {
	const recommendationRoomState = recommendationState?.findLowestPriceForLogRoom(item.pricingArchitectureRoomId);

	return (
		<tr className={isAnchor === true ? 'bg-sky-50' : undefined}>
			<td className="whitespace-nowrap px-3 py-2 font-medium text-gray-700">
				{room ? <PricingRoomName room={room} /> : `Room #${item.pricingArchitectureRoomId}`}
				{isAnchor === true && (
					<span className="block font-light text-xs italic text-gray-400">
						(Base Price{roomType !== undefined && roomType !== '' ? ` - ${roomType}` : ''})
					</span>
				)}
				{isAnchor !== true &&
					derivativeDescription !== undefined &&
					derivativeDescription !== null && (
						<span className="block font-light text-xs italic text-gray-400">
							({derivativeDescription})
						</span>
					)}
			</td>
			<td className="px-3 py-2 text-gray-700">
				{formatPricingSubmissionItemAmount(companyHotel, item, 'basePrice')}
			</td>
			<td className="px-3 py-2 text-gray-500">
				{formatRecommendationAmount(companyHotel, recommendationRoomState, 'finalPrice')}
			</td>
			<td className="px-3 py-2 text-gray-700">
				{Utilities.formatAmount(item.applyPrice, companyHotel.settings.main.currency, 0)}
			</td>
			<td className="px-3 py-2 text-gray-500">
				{recommendationRoomState !== undefined && (
					<RenderSubmittedDifferenceRate
						basePrice={recommendationRoomState.basePrice}
						submittedPrice={item.applyPrice}
					/>
				)}
			</td>
		</tr>
	);
};

const formatRecommendationAmount = (
	companyHotel: CompanyHotel,
	recommendationRoomState: PriceRecommendationRoomState | undefined,
	key: 'basePrice' | 'finalPrice'
) => {
	if (recommendationRoomState === undefined) {
		return '-';
	}

	return Utilities.formatAmount(
		recommendationRoomState[key],
		companyHotel.settings.main.currency,
		0
	);
};

const formatPricingSubmissionItemAmount = (
	companyHotel: CompanyHotel,
	pricingSubmissionItem: PricingSubmissionItem | undefined,
	key: 'basePrice' | 'applyPrice'
) => {
	if (pricingSubmissionItem === undefined) {
		return '-';
	}

	return Utilities.formatAmount(
		pricingSubmissionItem[key],
		companyHotel.settings.main.currency,
		0
	);
};

const RenderSubmittedDifferenceRate: React.FunctionComponent<{
	basePrice: number;
	submittedPrice: number;
}> = ({ basePrice, submittedPrice }) => {
	if (basePrice === 0) {
		return null;
	}

	const rate = Utilities.roundDouble(
		((submittedPrice - basePrice) / basePrice) * 100,
		0
	);
	if (rate === 0) {
		return null;
	}

	return rate > 0 ? (
		<div className="inline-flex gap-x-0.5 items-center text-green-500">
			<ArrowUpIcon className="w-3 h-3" />
			<span>+{rate}%</span>
		</div>
	) : (
		<div className="inline-flex gap-x-0.5 items-center text-red-500">
			<ArrowDownIcon className="w-3 h-3" />
			<span>{rate}%</span>
		</div>
	);
};

const renderDerivativeDescription = (
	companyHotel: CompanyHotel,
	type: PricingArchitectureDerivativeRuleType,
	value: number | undefined,
	roomType: string
) => {
	if (type === PricingArchitectureDerivativeRuleType.SAME) {
		return `Equal to base price - ${roomType}`;
	}

	if (type === PricingArchitectureDerivativeRuleType.AMOUNT) {
		const ruleValue = value ?? 0;
		return (
			<span>
				{ruleValue > 0 ? '+' : ''}
				{ruleValue}
				{Utilities.formatCurrency(companyHotel.settings.main.currency)} from base price - {roomType}
			</span>
		);
	}

	if (type === PricingArchitectureDerivativeRuleType.PERCENTAGE) {
		const ruleValue = value ?? 0;
		return (
			<span>
				{ruleValue > 0 ? '+' : ''}
				{ruleValue}% from base price - {roomType}
			</span>
		);
	}

	return undefined;
};

export default PriceSubmissionLogTableFragment;
